import { useEffect, useState } from 'preact/hooks'

import { api } from '../api.js'
import { Avatar } from '../Avatar.js'
import { t } from '../i18n/t.js'
import { onEvent } from '../ws.js'
import { melden, schliessen, verdeckt } from '../meldung.js'
import { aus, klingelnAn } from '../ton.js'
import {
  beimKlingeln, anrufUebergeben, klingelnSetzen, klingeltJetzt, type Klingelt,
} from './klingeln.js'

/**
 * Die Kachel für einen eingehenden Anruf — auf **jedem** Bildschirm.
 *
 * Sie hing bis 2026-09-10 in `routes/Anruf.tsx` und damit an der Bedingung,
 * dass man gerade auf dem Reiter „Anrufe" steht. Wer im Chat war oder auf der
 * Familienseite, sah von einem Anruf nichts. Genau deshalb ging von Gerät zu
 * Gerät praktisch nie einer durch.
 *
 * Jetzt hängt sie in `App.tsx`, neben dem SOS-Knopf: an einer Stelle, die
 * nicht davon abhängt, was gerade offen ist. Sie liegt bewusst **nicht** im
 * nachgeladenen Anruf-Bündel — was klingeln soll, darf nicht erst geladen
 * werden müssen.
 *
 * Was sie nicht kann: ein Gerät mit dunklem Bildschirm wecken. Dafür bräuchte
 * es eine Push-Nachricht für Anrufe, und die gibt es serverseitig nicht.
 */
export function EingehenderAnruf({ meId }: { meId: string }) {
  const [klingelt, setKlingelt] = useState<Klingelt | null>(klingeltJetzt)

  useEffect(() => beimKlingeln(setKlingelt), [])

  /*
   * Der Zuhörer für das Klingeln selbst.
   *
   * Er sitzt in diesem Bauteil und nicht im Speicher-Modul, weil `onEvent`
   * eine Anmeldung am WebSocket ist, die wieder gelöst werden muss. Ein Modul,
   * das sich beim Import irgendwo anmeldet, tut das auch in jedem Test.
   */
  useEffect(() => onEvent((e) => {
    if (e.type === 'call.incoming') {
      // Der eigene Anruf klingelt nicht beim Anrufer.
      if (e.ref?.fromPersonId === meId) return
      klingelnSetzen({
        callId: String(e.ref?.callId),
        von: String(e.ref?.fromPersonId),
        kind: String(e.ref?.kind),
      })
    }
    if (e.type === 'call.ended') {
      const jetzt = klingeltJetzt()
      if (jetzt && jetzt.callId === e.ref?.callId) klingelnSetzen(null)
    }
  }), [meId])

  /*
   * Klingeln, solange die Kachel steht.
   *
   * Hier und nicht in der Kachel selbst: Die Kachel baut sich neu auf, sobald
   * der Name der anrufenden Person nachgeladen ist — der Ton finge dann von
   * vorn an. An dieser Stelle hängt er am Klingeln, nicht an der Anzeige.
   */
  useEffect(() => {
    if (!klingelt) return
    klingelnAn()
    /*
     * Und zusätzlich eine Systemmeldung, wenn niemand hinschaut.
     *
     * Bei sichtbarer App wäre sie Doppelung — die Kachel steht ja da. Liegt
     * das Telefon aber mit dunklem Bildschirm daneben, ist sie der einzige
     * Weg, der noch trägt: Der Ton in der Seite wird dann gedrosselt.
     */
    if (verdeckt()) {
      melden({
        titel: t('Anruf'),
        text: t('Jemand aus deiner Familie ruft an.'),
        marke: `anruf-${klingelt.callId}`,
      })
    }
    return () => { aus(); schliessen() }
  }, [klingelt?.callId])

  if (!klingelt) return null
  return <Kachel klingelt={klingelt} />
}

function Kachel({ klingelt }: { klingelt: Klingelt }) {
  const [name, setName] = useState(t('Jemand'))
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void api.get<Array<{ id: string; displayName: string }>>('/family/members')
      .then((m) => setName(m.find((p) => p.id === klingelt.von)?.displayName ?? t('Jemand')))
      .catch(() => undefined)
  }, [klingelt.von])

  async function annehmen() {
    setBusy(true)
    try {
      const call = await api.post(`/calls/${klingelt.callId}/join`, {})
      // Erst ablegen, dann hingehen: Der Anrufbildschirm holt ihn beim
      // Aufbauen ab und springt sofort ins Gespräch.
      anrufUebergeben(call)
      klingelnSetzen(null)
      location.hash = '#anrufen'
    } catch {
      klingelnSetzen(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div class="fh-card fh-anruf__eingehend" role="alert">
      {/* Große Kachel mit Namen — wie bei einem eingehenden Anruf üblich. */}
      <Avatar name={name} gross />
      <p class="fh-h2">
        <span aria-hidden="true">{klingelt.kind === 'video' ? '📹' : '📞'}</span>
        {' '}{t('{wer} ruft an', { wer: name })}
      </p>
      <p class="fh-muted">
        {klingelt.kind === 'video' ? t('Videoanruf') : t('Sprachanruf')}
      </p>

      <div class="fh-anruf__knoepfe">
        {/* Groß, weit auseinander, mit Text. Wer ein klingelndes Telefon in
            der Hand hält, trifft keine feinmotorischen Entscheidungen. */}
        <button
          class="fh-btn fh-btn--gross fh-btn--annehmen"
          disabled={busy}
          onClick={() => void annehmen()}
        >
          <span aria-hidden="true">✅</span> {t('Annehmen')}
        </button>
        <button
          class="fh-btn fh-btn--gross fh-btn--gefahr"
          onClick={() => {
            void api.post(`/calls/${klingelt.callId}/reject`, {}).catch(() => undefined)
            klingelnSetzen(null)
          }}
        >
          <span aria-hidden="true">❌</span> {t('Ablehnen')}
        </button>
      </div>
    </div>
  )
}
